/**
 * ./exercise8/Exercise8Workout.js
 *
 * This module helps users improve **speed reading** using a **progressive phrase flashing technique**.
 *
 * **Key Features:**
 * -------------------------------------
 * - Choose a Reading Level (2-10 words per phrase).
 * - Select or Upload an Article.
 * - 10-Minute Timed Flash Reading Session.
 * - Words flash progressively, 
 * - Auto-Redirect to a Comprehension Test.
 *
 */


import React, { useState, useEffect } from "react";
import "./Exercise8Workout.css"; // Import the CSS file
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "../components/icons/close.svg";
import InfoIcon from "../components/icons/InfoIcon.svg";
import LibraryButton from "../exercise7/icons/LibraryButton.svg";
import UploadButton from "../exercise7/icons/UploadButton.svg";
import PlayButton from "../components/icons/play.svg";
import UpButton from "../components/icons/UpButton.svg";
import DownButton from "../components/icons/DownButton.svg";
import articles from "../exercise7/data/articles";
import { updateUserProgress } from '../utility';
import { DEBUG_TIME, API_BASE_URL } from "../config";

const Exercise8Workout = () => {
  const navigate = useNavigate();
  const TIMER_DURATION = DEBUG_TIME || 60;
  const location = useLocation();
  const isWarmup = location.state?.isWarmup;
  const exerciseNumber = location.state?.exerciseNumber;
  const [exerciseStep, setExerciseStep] = useState(() => isWarmup ? "Choose Article" : "Select Level");
  const [text, setText] = useState("No Text");
  const [progress, setProgress] = useState(0);
  const [phrasesIndex, setPhrasesIndex] = useState(0);
  const [paragraphIndex, setParagraphIndex] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const [flashCount, setFlashCount] = useState(0);
  const [articleIndex, setArticleIndex] = useState(0);
  const [endArticleIndex, setEndArticleIndex] = useState(4);
  const [levelSelection, setLevelSelection] = useState(() => isWarmup ? 1.5 : 2);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [articleName, setArticleName] = useState("");
  const [userData, setUserData] = useState(null);
  const levels = [2, 4, 6, 8, 10];


  const phraseSplitter = () => {
    const words = [];
    const paragraphs = [];
    for (let i = 0; i < text.length; i++) {
      words.push(text[i].split(" "));
      let paragraphPhrases = [];
      for (let j = 0; j < words[i].length; j += levelSelection) {
        paragraphPhrases.push(words[i].slice(j, j + levelSelection).join(" "));
      }
      paragraphPhrases.push("");
      paragraphs.push(paragraphPhrases);
    }
    return paragraphs;
  };

  let paragraphs = phraseSplitter(text);


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const isLevelUnlocked = (levelNumber) => {
    if (!userData) return false;
    if ((8 < userData.numExercisesUnlocked) || (8 === userData.numExercisesUnlocked && levelNumber <= userData.currNumLevelsUnlocked)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (
      isFlashing &&
      paragraphIndex < paragraphs.length &&
      phrasesIndex < paragraphs[paragraphIndex].length - 1 &&
      timeElapsed < TIMER_DURATION
    ) {
      const intervalId = setInterval(() => {
        setPhrasesIndex((prevIndex) => prevIndex + 1);
        setTimeElapsed((prevTime) => {
          const newTime = prevTime + 0.3;
          setProgress((newTime / TIMER_DURATION) * 100);
          return newTime;
        });

        if (
          paragraphIndex < paragraphs.length - 1 &&
          phrasesIndex == paragraphs[paragraphIndex].length - 2
        ) {
          setPhrasesIndex(0);
          setParagraphIndex((prevIndex) => prevIndex + 1);
        }
      }, 300);

      return () => clearInterval(intervalId);
    }

    if (timeElapsed >= TIMER_DURATION) {
      setIsFlashing(false);
      updateUserProgress(8, levelSelection, 0);
      const timeoutId = setTimeout(() => {
        if (isWarmup) {
            navigate("/Warmupfinish", {
              state: {
                exerciseNumber: exerciseNumber
              }
            });

        } else {
            navigate("/exercise7test", {
              state: {
                levelSelection: levelSelection,

                articleText: text,
                articleName: articleName,
              }
            });
        }

      }, 500);
  
      return () => clearTimeout(timeoutId);
    }
  }, [isFlashing, phrasesIndex, paragraphs.length, timeElapsed, navigate, text, articleName]);

  const handleClose = () => {
    navigate("/");
  };

  const goToLibrary = () => {
    setExerciseStep("Choose Article");
  };

  const clickBook = (articleText, articleName) => {
    setText(articleText);
    setExerciseStep("Workout");
    setArticleName(articleName);
  };

  const playWordFlasher = () => {
    setIsFlashing(true);
  };

  const IncrementArticleDown = () => {
    console.log(articleIndex);
    let prevArticleIndex = articleIndex;
    if (articleIndex - 4 >= 0) {
      setArticleIndex(articleIndex - 4);
      setEndArticleIndex(endArticleIndex - 4);
    } else {
      setArticleIndex(0);
      setEndArticleIndex(endArticleIndex - prevArticleIndex);
    }
    console.log(articleIndex);
  };

  const IncrementArticleUp = () => {
    console.log(articleIndex);
    if (articleIndex + 4 < articles.length) {
      setArticleIndex(articleIndex + 4);
      setEndArticleIndex(endArticleIndex + 4);
    } else {
      setArticleIndex(articles.length - 4);
      setEndArticleIndex(articles.length - 1);
    }
    console.log(articleIndex);
  };

  const renderExercise = () => {
    if (exerciseStep == "Select Level") {
      return (
        <>
          <h3>Pick Your Level</h3>
          <div>
            {levels.map((level, index) => (
              <button
                key={level}
                className={`duration-button ${levelSelection === level ? "selected" : ""}`}
                onClick={() => setLevelSelection(level)}
                disabled={!isLevelUnlocked(index + 1)}
                style={{
                  backgroundColor: isLevelUnlocked(index + 1) ? "#4CAF50" : "#CCCCCC",
                  cursor: isLevelUnlocked(index + 1) ? "pointer" : "not-allowed"
                }}
              >
                {level}
              </button>
            ))}
          </div>
          <button
            className="next-button"
            onClick={() => setExerciseStep("Upload File")}
          >
            Next
          </button>
        </>
      );
    }
    if (exerciseStep == "Upload File") {
      return (
        <div className="file-upload-container">
          <h3>Upload or Choose from Library</h3>
          <div>
            <img src={UploadButton} alt="upload button" />{" "}
            {/* NEXT STEP: file upload function */}
            <img
              src={LibraryButton}
              alt="upload button"
              onClick={goToLibrary}
              className="library-button"
            />
          </div>
        </div>
      );
    }
    if (exerciseStep == "Choose Article") {
      return (
        <div>
          <h3>Choose an Unread Article</h3>
          <div className="article-picker">
            <img
              src={DownButton}
              className="library-button"
              onClick={IncrementArticleDown}
            />
            {articles.slice(articleIndex, endArticleIndex).map((article) =>
              article.read ? (
                <>
                  <p className="text-overlay">completed</p>
                  <img
                    src={article.image}
                    alt="article image"
                    className={"bookCover read"}
                  ></img>
                </>
              ) : (
                <img
                  src={article.image}
                  alt="article image"
                  className={"bookCover"}
                  onClick={() => clickBook(article.text, article.name)}
                ></img>
              )
            )}
            <img
              src={UpButton}
              className="library-button"
              onClick={IncrementArticleUp}
            />
          </div>
        </div>
      );
    }
    if (exerciseStep == "Workout") {
      return (
        <div>
          {paragraphs.map((paragraph, paragraphsIndex) => (
            <div className="article-reader">
              {paragraph.map((word, wordIndex) => (
                <div className="phrase">
                  {word}
                  <div
                    className={`text-cover ${
                      isFlashing &&
                      wordIndex == phrasesIndex &&
                      paragraphsIndex == paragraphIndex
                        ? "display-none"
                        : ""
                    }`}
                  ></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="exercise-container">
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img
            src={CloseIcon}
            alt="close icon"
            className="close-icon"
            onClick={handleClose}
          />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="close-icon-wrapper">
          <img
            src={PlayButton}
            alt="play button"
            onClick={playWordFlasher}
            width="50px"
          />
        </div>
      </div>
      <div className="main-content-8">{renderExercise()}</div>
      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        {exerciseStep !== "Workout" ? (
          <p>Upload or choose an article you have not read before.</p>
        ) : (
          <p>Focus on the flashing words.</p>
        )}
      </div>
    </div>
  );
};
export default Exercise8Workout;